import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Printer, AlertTriangle } from 'lucide-react';

const EFFECTIVE_DATE = 'February 12, 2026';
const LAST_UPDATED = 'February 12, 2026';

const SECTIONS = [
  {
    id: 'agreement',
    heading: 'Agreement',
    body: [
      "These Terms of Service ('Terms') govern your access to and use of Brotherhood+ ('we', 'us', 'our'), including brotherhoodplus.com and any related web and mobile apps (the 'Service').",
      "By creating an account, checking the acknowledgment box, or using the Service, you agree to these Terms and to our Privacy Policy. If you do not agree, do not use the Service.",
    ],
  },
  {
    id: 'eligibility',
    heading: '1. Eligibility & account responsibilities',
    body: [
      "You must be at least 18 years old to use Brotherhood+.",
      "You are responsible for keeping your account credentials confidential. Enable two-factor authentication (Profile → Security) for stronger protection. You are responsible for activity that occurs under your account.",
      "You agree to provide accurate information when creating your account and to keep it current.",
    ],
  },
  {
    id: 'acceptable-use',
    heading: '2. Acceptable use',
    body: [
      "You agree NOT to:",
      { list: [
        "Use the Service for any unlawful purpose or in violation of any regulation.",
        "Impersonate another person or misrepresent your affiliation.",
        "Harass, threaten, dox, or discriminate against any user, coach, or clinician.",
        "Scrape, crawl, or extract data using automated tools without our written consent.",
        "Attempt to bypass, disable, or interfere with security features, rate limits, or authentication.",
        "Upload malware, phishing content, or content that infringes another person's rights.",
        "Resell, rent, or commercialize your access to the Service.",
      ] },
    ],
  },
  {
    id: 'ai',
    heading: '3. AI-generated content',
    body: [
      "The AI Coach and AI Journal reflections are generated by large language models. AI responses:",
      { list: [
        "May be inaccurate, incomplete, or out of date.",
        "Do not constitute professional advice of any kind (medical, therapeutic, legal, financial, or otherwise).",
        "Should be verified independently before acting on them, especially for health-related decisions.",
        "Are not confidential in the way a session with a licensed therapist would be. Prompts you send are processed by third-party AI providers under standard commercial data-processing terms.",
      ] },
      "We do our best to add safety layers — crisis-language detection, referrals to professional help, and a persistent reminder that AI is a companion, not a clinician — but you remain responsible for your decisions.",
    ],
  },
  {
    id: 'medical',
    heading: '4. Mental-health & medical disclaimer',
    body: [
      { note: "Brotherhood+ is not a medical device, healthcare provider, or therapy service." },
      "Brotherhood+ provides self-directed wellness content, tools, and community. It is not a substitute for advice, diagnosis, or treatment from a licensed clinician. If you have a medical or mental-health condition, consult a qualified professional.",
      "Content on the Service (including AI-generated content, articles, lessons, assessments, and journal reflections) is provided for general informational and self-reflection purposes only.",
    ],
  },
  {
    id: 'crisis',
    heading: '5. Crisis & emergency disclaimer',
    body: [
      { note: "Brotherhood+ IS NOT AN EMERGENCY SERVICE. Do not use it during a crisis." },
      "If you or someone you know is in immediate danger, in crisis, or considering self-harm, please contact your local emergency services or a crisis line right now:",
      { list: [
        "United States: dial or text 988 (Suicide & Crisis Lifeline)",
        "International: visit findahelpline.com for a directory of local crisis lines",
        "For life-threatening emergencies, call your local emergency number (911 in the U.S., 999 in the U.K., 112 in the EU, 000 in Australia, etc.)",
      ] },
    ],
  },
  {
    id: 'community',
    heading: '6. Community conduct',
    body: [
      "Brotherhood+ community spaces (including any future messaging, groups, or events) are moderated for safety. We reserve the right to remove content or restrict accounts that:",
      { list: [
        "Threaten, harass, or discriminate against others.",
        "Share personal information about another user without consent.",
        "Promote violence, self-harm, or illegal activity.",
        "Solicit clinical advice from other members inappropriately.",
      ] },
    ],
  },
  {
    id: 'ugc',
    heading: '7. User-generated content',
    body: [
      "You retain ownership of the content you create on the Service — journal entries, check-ins, community posts, assessments. You grant us a limited, non-exclusive, worldwide license to store, display, and process that content solely to operate the Service and provide it back to you.",
      "You represent that you have the rights to any content you submit and that it does not infringe on any third party's rights.",
    ],
  },
  {
    id: 'ip',
    heading: '8. Intellectual property',
    body: [
      "Brotherhood+, the Brotherhood+ logo, our lessons, growth plans, assessments, and product design are owned by us and protected by intellectual property laws. Your account gives you a personal, non-transferable, revocable license to use the Service. Nothing in these Terms transfers our intellectual property to you.",
    ],
  },
  {
    id: 'subscriptions',
    heading: '9. Subscriptions, payment, cancellation & refunds',
    body: [
      { subheading: 'Free access', text: "All Brotherhood+ features are currently available at no cost while we complete account setup and select a payment provider." },
      { subheading: 'Future paid tiers', text: "We plan to offer optional paid memberships in the future. When they launch, pricing, billing cadence, included features, cancellation, and refund terms will be displayed at checkout and this section will be updated before any charges are made." },
      { subheading: 'No current billing', text: "No payment provider is presently connected to the Service. We are not collecting or storing card data at this time." },
    ],
  },
  {
    id: 'termination',
    heading: '10. Account suspension & termination',
    body: [
      "We may suspend or terminate your account if you violate these Terms, engage in fraudulent activity, or if we're required to by law. Where safe and lawful, we will notify you first.",
      "You may terminate your account at any time from Profile → Delete account. Your data will be deleted per the retention rules in our Privacy Policy.",
    ],
  },
  {
    id: 'warranty',
    heading: '11. Warranties & liability limitations',
    body: [
      "THE SERVICE IS PROVIDED 'AS IS' AND 'AS AVAILABLE' WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.",
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW, BROTHERHOOD+ AND ITS SUPPLIERS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF THE SERVICE.",
      "OUR AGGREGATE LIABILITY IS LIMITED TO THE AMOUNTS YOU HAVE PAID US FOR THE SERVICE IN THE 12 MONTHS PRECEDING THE CLAIM, OR US$100, WHICHEVER IS GREATER. Some jurisdictions do not allow the exclusion or limitation of certain damages; in those places, these limitations apply only to the extent permitted by law.",
    ],
  },
  {
    id: 'indemnification',
    heading: '12. Indemnification',
    body: [
      "You agree to defend, indemnify, and hold harmless Brotherhood+ and its officers, directors, employees, and agents from any claims, damages, and expenses (including legal fees) arising out of or in connection with (a) your use of the Service, (b) your violation of these Terms, or (c) your violation of any third-party right, including intellectual property or privacy rights.",
    ],
  },
  {
    id: 'governing-law',
    heading: '13. Governing law & disputes',
    body: [
      "These Terms are governed by the laws of the State of [Governing State — to be finalized by counsel], United States, without regard to conflict-of-laws principles. Any dispute arising from these Terms or the Service will be resolved in the state or federal courts located in [County, State], and you consent to personal jurisdiction there.",
      "Nothing in this section prevents either party from seeking injunctive or equitable relief in any court of competent jurisdiction.",
    ],
  },
  {
    id: 'changes',
    heading: '14. Changes to these Terms',
    body: [
      "We may update these Terms from time to time. When we do, we will update the 'Last updated' date at the top and, for material changes, notify you by email or an in-app notice at least 14 days before the change takes effect. Continued use of the Service after the effective date constitutes acceptance of the updated Terms.",
    ],
  },
  {
    id: 'contact',
    heading: '15. Contact us',
    body: [
      "Questions about these Terms:",
      { list: [
        "Email: brotherhoodplusapp@gmail.com",
      ] },
    ],
  },
];

function renderBody(item, key) {
  if (typeof item === 'string') return <p key={key} className="mb-3 leading-relaxed text-navy/80">{item}</p>;
  if (item.list) return <ul key={key} className="list-disc list-outside ml-6 mb-3 space-y-1 text-navy/80">{item.list.map((li, i) => <li key={i} className="leading-relaxed">{li}</li>)}</ul>;
  if (item.subheading) return (
    <div key={key} className="mb-3">
      <p className="font-semibold text-navy">{item.subheading}</p>
      <p className="mt-1 leading-relaxed text-navy/80">{item.text}</p>
    </div>
  );
  if (item.note) return (
    <div key={key} className="my-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 flex items-start gap-2.5">
      <AlertTriangle size={16} className="mt-0.5 shrink-0"/>
      <p className="font-semibold text-sm leading-snug">{item.note}</p>
    </div>
  );
  return null;
}

export default function Terms() {
  useEffect(() => {
    document.title = 'Terms of Service · Brotherhood+';
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'description'); document.head.appendChild(m); }
    m.setAttribute('content', 'The rules for using Brotherhood+. Effective February 12, 2026.');
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F8F6] print:bg-white" data-testid="terms-page">
      <style>{`@media print { .no-print { display: none !important; } body { background: white; } main { max-width: 720px !important; } }`}</style>
      <div className="max-w-3xl mx-auto p-6 lg:p-10">
        <div className="flex items-center justify-between mb-6 no-print">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-smooth" data-testid="terms-back">
            <ArrowLeft size={14}/> Home
          </Link>
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 text-xs text-navy/60 hover:text-navy rounded-full border border-navy/15 px-3 py-1.5" aria-label="Print this page" data-testid="terms-print">
            <Printer size={13}/> Print
          </button>
        </div>

        <main>
          <header className="mb-8">
            <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">Brotherhood+</p>
            <h1 className="font-heading text-4xl lg:text-5xl mt-2 leading-tight">Terms of Service</h1>
            <p className="mt-3 text-sm text-navy/55">Effective: {EFFECTIVE_DATE} · Last updated: {LAST_UPDATED}</p>
          </header>

          <nav aria-label="Table of contents" className="mb-8 rounded-2xl bg-white p-5 card-soft no-print">
            <p className="text-[11px] uppercase tracking-widest text-navy/50 font-semibold mb-2">On this page</p>
            <ol className="grid sm:grid-cols-2 gap-1.5 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.id}><a href={`#${s.id}`} className="text-navy hover:text-gold transition-smooth">{s.heading}</a></li>
              ))}
            </ol>
          </nav>

          <div className="space-y-8">
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-20" data-testid={`terms-section-${s.id}`}>
                <h2 className="font-heading text-2xl mb-3">{s.heading}</h2>
                <div>{s.body.map((item, i) => renderBody(item, i))}</div>
              </section>
            ))}
          </div>

          <footer className="mt-12 pt-6 border-t border-navy/10 text-sm text-navy/55">
            <p>Brotherhood+ · Rooted in culture. Made for every man ready to grow.</p>
            <p className="mt-1">Also read: <Link to="/privacy" className="underline hover:text-navy">Privacy Policy</Link></p>
          </footer>
        </main>
      </div>
    </div>
  );
}
